import { Component, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { NoteView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { NoteService } from '@http/clients/note.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { Note } from '@models/Note';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    SnackbarNotificationService,
    ValidatorType
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

interface IModalEditNote {
    note?: NoteView;
    entityTypeId: EntityTypes;
    entityId: number;
    notificationTitle?: string;
    organizationId?: number;
}

@Component({
    selector: 'app-modal-edit-note',
    templateUrl: './modal-edit-note.component.html',
    styleUrls: ['./modal-edit-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalEditNoteComponent extends ModalComponentBase<IModalEditNote, NoteView> implements OnInit {
    protected override parameters!: IModalEditNote;

    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    note!: Note;

    @ViewChildren('editorItem')
    editors?: QueryList<EditorItemComponent>;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly noteService: NoteService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly awaiter: AwaiterService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.configItems = [];
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
    }

    ngOnInit(): void {
        this.note = (this.parameters.note && Note.fromInterface(this.parameters.note)) || new Note();
        this.note.entityId = this.parameters.entityId;
        this.note.entityTypeId = this.parameters.entityTypeId;
        this.note.notificationTitle = this.parameters.notificationTitle;
        this.note.organizationId = this.parameters.organizationId;
        this.configItems = [
            {
                label: 'Note Title',
                componentId: EditorType.textInput,
                fieldName: 'title',
                bindContext: this.note,
                validations: [
                    { type: ValidatorType.required },
                    { type: ValidatorType.string, args: { maxLength: 200 } }
                ],
                additionalInfo: {
                    shouldTrimSpaces: true
                }
            },
            {
                label: 'Addressed to',
                componentId: EditorType.noteUser,
                fieldName: 'noteUsers',
                bindContext: this.note
            },
            {
                componentId: EditorType.textarea,
                fieldName: 'message',
                bindContext: this.note,
                validations: [
                    { type: ValidatorType.required },
                    { type: ValidatorType.string, args: { maxLength: 70000 } }
                ],
                additionalInfo: {
                    shouldTrimSpaces: true
                }
            }
        ];
    }

    validate(): boolean {
        return this.editors?.map((editorItem) => editorItem.validate()).every((editorItem) => editorItem) ?? false;
    }

    async saveNote(): Promise<void> {
        if (!this.validate()) return;

        await this.awaiter.awaitAction(
            'Saving Note...',
            async () => {
                const response = await this.noteService.save(this.note);
                if (response)
                    this.snackbarNotificationService.success(
                        this.parameters.note ? 'Note edited successfully.' : 'Note created successfully.'
                    );
                this.close(response);
            },
            (loading) => (this.isLoading = loading)
        );
    }
}
